import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CreateOpportunityComponent } from '@fitness-force/crm';
import {
  TaskCreateComponent,
  RouteStateService,
} from '@fitness-force/shared';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { EditorModule } from 'primeng/editor';
import { FileUploadModule } from 'primeng/fileupload';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { InputTextModule } from 'primeng/inputtext';
import { OverlayModule } from 'primeng/overlay';
import { PopoverModule } from 'primeng/popover';
import { TableModule } from 'primeng/table';
import { TreeSelectModule } from 'primeng/treeselect';
import { SendBulkEmailComponent } from '../../send-bulk-email/send-bulk-email.component';
import { SendBulkSmsComponent } from '../../send-bulk-sms/send-bulk-sms.component';
import { SendWhatsappComponent } from '../../send-whatsapp/send-whatsapp.component';
import {
  Actions,
  ActionsGroup,
  AutomationActionPayload,
  AutomationActions,
  AutomationActionsGroup,
  AutomationByIdResponse,
  AutomationData,
  AutomationMaster,
  AutomationTableData,
  City,
  CompanyInfoStoreInterface,
  Country,
  CreateAutomation,
  CustomAutomationData,
  EmailActionPayload,
  NotificationToStaff,
  Params,
  Rule,
  ScheduleActionPayload,
  SmsActionPayload,
  SourceDataExtractionRules,
  State,
  TENANTSINTERFACE,
  TENANT_MODEL,
  TaskActionPayload,
  UpdateAutomation,
  WhatsappActionPayload,
} from '@fitness-force/models';
import {
  SendNotificationTo,
  CampaignSource,
  EmailSendType,
  SMSSendType,
  TaskCreateType,
  WhatsAppSendType,
  OwnershipLevel,
  AutomationEventType,
} from '@fitness-force/enum';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  AutomationmasterService,
  CommonService,
  TimeconversionService,
  ToastMessageService,
} from '@fitness-force/api';
import { Store } from '@ngrx/store';
import { CampaignService } from 'libs/api/src/lib/campaign/campaign.service';
import { SkeletonModule } from 'primeng/skeleton';
import {
  COMPANY_INFO,
  TENANTS_INFO,
} from 'apps/ff-frontend/src/app/state/app.state';
import { BreadcrumbComponent } from '@fitness-force/common/breadcrumb';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { CustomAutomationPopupComponent } from '../custom-automation-popup/custom-automation-popup.component';
import { SelectAutomationEventPopupComponent } from '../select-automation-event-popup/select-automation-event-popup.component';
import { RuleBuilderPopupComponent } from '../rule-builder-popup/rule-builder-popup.component';
import { StaffNotificationSettingPopupComponent } from '../staff-notification-setting-popup/staff-notification-setting-popup.component';
import { ConfirmEventType, ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MessageModule } from 'primeng/message';

// Replace Mobiscroll imports with Syncfusion
import {
  ScheduleModule,
  DayService,
  WeekService,
  WorkWeekService,
  MonthService,
  AgendaService,
  MonthAgendaService,
  TimelineViewsService,
  TimelineMonthService,
  ScheduleComponent,
  EventClickArgs,
  View,
  EventRenderedArgs,
  EventSettingsModel
} from '@syncfusion/ej2-angular-schedule';

// Add this interface after the imports 
interface CalendarEventModel {
  id: string;
  subject: string;
  startTime: Date;
  endTime: Date;
  color?: string;
  text?: string;
}

@Component({
  selector: 'fitness-force-create-automation',
  templateUrl: './create-automation.component.html',
  styleUrls: ['./create-automation.component.css'],
  providers: [
    DayService,
    WeekService,
    WorkWeekService,
    MonthService,
    AgendaService,
    MonthAgendaService,
    TimelineViewsService,
    TimelineMonthService, 
    DialogService
  ],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    ToggleSwitchModule,
    InputTextModule,
    DatePickerModule,
    SelectModule,
    EditorModule,
    ButtonModule,
    FileUploadModule,
    OverlayModule,
    PopoverModule,
    CheckboxModule,
    TableModule,
    TreeSelectModule,
    CreateOpportunityComponent,
    SendBulkEmailComponent,
    SendBulkSmsComponent,
    SendWhatsappComponent,
    TaskCreateComponent,
    SkeletonModule,
    BreadcrumbComponent,
    StaffNotificationSettingPopupComponent,
    ConfirmDialogModule,
    TranslateModule,
    MessageModule,
    ScheduleModule // Add Syncfusion ScheduleModule
  ]
})
export class CreateAutomationComponent implements OnInit {
  @ViewChild('scheduleObj') public scheduleObj!: ScheduleComponent;
  showActionGroup1: boolean = true;
  showTable: boolean = true;
  actionsTableData: any[] = [];
  tenants: any[] = [];
  selectedTenant: { name: string } | undefined;
  tenantAccess: TENANT_MODEL[] = [];
  state$: State[] = [];
  city$: City[] = [];
  country$: Country[] = [];

  createAutomationForm: UntypedFormGroup = new UntypedFormGroup({});
  Actions = [
    {
      text: 'SEND_EMAIL_CLIENT',
      key: 'sendemail',
      notificationTo: SendNotificationTo.Client,
    },
    {
      text: 'SEND_SMS_CLIENT',
      key: 'sendsms',
      notificationTo: SendNotificationTo.Client,
    },
    {
      text: 'SEND_WHATSAPP_CLIENT',
      key: 'sendwhatsapp',
      notificationTo: SendNotificationTo.Client,
    },
    {
      text: 'SEND_EMAIL_STAFF',
      key: 'sendemail',
      notificationTo: SendNotificationTo.Staff,
    },
    {
      text: 'SEND_SMS_STAFF',
      key: 'sendsms',
      notificationTo: SendNotificationTo.Staff,
    },
    {
      text: 'SEND_WHATSAPP_STAFF',
      key: 'sendwhatsapp',
      notificationTo: SendNotificationTo.Staff,
    },
    {
      text: 'CREATE_TASK_STAFF',
      key: 'createtask',
      notificationTo: SendNotificationTo.Staff,
    },
  ];

  values: string[] = ['Mr.', 'Mrs.'];
  showCreateOpportunityPopup: boolean = false;
  showTaskCreate: boolean = false;
  showBulkEmailPopup: boolean = false;
  showBulkEmailPopup$ = new BehaviorSubject<boolean>(false);
  showBulkSmsPopup: boolean = false;
  showBulkWhatsAppPopup: boolean = false;

  EmailSendType: EmailSendType = EmailSendType.Automation;
  SMSSendType: SMSSendType = SMSSendType.Automation;
  WhatsAppSendType: WhatsAppSendType = WhatsAppSendType.Automation;
  taskCreateType: TaskCreateType = TaskCreateType.Automation;
  campaignSource: CampaignSource = CampaignSource.Rules;

  getAccessTenantList$: Observable<any> = new Observable();
  companyInfoDetail$: Observable<any> = new Observable();
  tenantId = 0;
  loggedInTenant: TENANT_MODEL = {} as TENANT_MODEL;
  btnLoading: boolean = false;
  emailActionPayload: EmailActionPayload | undefined; //= new BehaviorSubject<EmailActionPayload>({} as EmailActionPayload);
  smsActionPayload: SmsActionPayload | undefined; // new BehaviorSubject<SmsActionPayload | null>({} as SmsActionPayload);
  whatsAppActionPayload: WhatsappActionPayload | undefined; // = new BehaviorSubject<WhatsappActionPayload>({} as WhatsappActionPayload);
  taskActionPayload: TaskActionPayload | undefined; //= new BehaviorSubject<TaskActionPayload>({} as TaskActionPayload);

  showGridView: boolean = false;
  showListView: boolean = true;
  

  automationMasters: AutomationMaster[] = [];
  // calendarOptions: MbscEventcalendarOptions; // Remove Mobiscroll calendar options
  // calView: MbscEventcalendarView; // Remove Mobiscroll calendar view
  
  // Syncfusion specific properties
  public eventSettings: EventSettingsModel = { 
    dataSource: [],
    fields: {
      id: 'Id',
      subject: { name: 'Subject' },
      startTime: { name: 'StartTime' },
      endTime: { name: 'EndTime' }
    }
  };
  public currentView: View = 'Month';
  
  actionPayloads: AutomationData[] = [];
  actionsTableDataNew: AutomationTableData[] = [];
  calendarEvents$ = new BehaviorSubject<CalendarEventModel[]>([]);
  formGroup: any = new BehaviorSubject<any>(null);
  uuid: string = '';
  clearbtnLoading: boolean = false;
  calenderDefaultdata = new Date(2023, 4, 1);
  currentDate: any = new Date();
  view = 'calendar';
  selectedNotificationTo: SendNotificationTo = SendNotificationTo.Client;
  automationData: CustomAutomationData = {} as CustomAutomationData;
  refAutomationModal: DynamicDialogRef = {} as DynamicDialogRef;
  refEventTrigeersModal: DynamicDialogRef = {} as DynamicDialogRef;
  refQueryBuilderModal: DynamicDialogRef = {} as DynamicDialogRef;
  refAssociatedStaffModal: DynamicDialogRef = {} as DynamicDialogRef;

  // will remove later
  staffNotificationSettings: boolean = false;
  showStaffNotificationSettings: boolean = false;
  filterConditionsModal: boolean = false;
  firstTimeSelected: boolean = false;
  actionSelectionData: {
    action: string;
    notificationTo: SendNotificationTo;
    data: any;
    mode: string;
  } = {
    action: '',
    notificationTo: SendNotificationTo.Staff,
    data: undefined,
    mode: '',
  };
  companyDetails = new BehaviorSubject<any>(null);
  automationGuid: string = '';
  automationResponse: AutomationByIdResponse = {} as AutomationByIdResponse;
  // forms
  createAutomationPopupForm: UntypedFormGroup = new UntypedFormGroup({});
  scheduleInMin:number=0;
  mode:string="";
  showSkeleton$ = new BehaviorSubject<boolean>(true);

  constructor(
    private fb: FormBuilder,
    private store: Store,
    private timeService: TimeconversionService,
    private campaignsService: CampaignService,
    private cd: ChangeDetectorRef,
    private translateService: TranslateService,
    private toastService: ToastMessageService,
    private router: Router,
    private commonService: CommonService,
    private automationService: AutomationmasterService,
    private routeStateService: RouteStateService,
    private dialogService: DialogService,
    private confirmationService: ConfirmationService,
    private route: ActivatedRoute
  ) {
    this.actionsTableData = [
      {
        text: 'SEND_EMAIL_CLIENT',
        key: 'sendemail',
        notificationTo: '',
        icon: 'fa fa-envelope font-light',
        color: 'cyan-500',
        status: 'Not Done',
        configured: false,
      },
      {
        text: 'SEND_SMS_CLIENT',
        key: 'sendsms',
        notificationTo: '',
        icon: 'fa fa-comments',
        color: 'pink-500',
        status: 'Not Done',
        configured: false,
      },
      //{ text: 'SEND_WHATSAPP',  key: 'sendwhatsapp', icon:"fab fa-whatsapp", color:"green-500", status: "Not Done",configured:false},
      {
        text: 'CREATE_TASK_CLIENT',
        key: 'createtask',
        notificationTo: '',
        icon: 'fa fa-phone',
        color: 'orange-500',
        status: 'Not Done',
        configured: false,
      },
      //{ text: 'CREATE_OPPORTUNITY', key: 'createopportunity', icon:"fa fa-lightbulb", color:"indigo-500", status: "Not Done",configured:false},
    ];

    this.createAutomationForm = this.fb.group({
      automation_name: ['', [Validators.required]],
      campaign_action_date: [this.calculateTomorrowDate(), Validators.required],
      automation_description: ['', [Validators.required]],
      //selected_tenant: ['', [Validators.required]],
      automation_type: ['', [Validators.required]],
      rule: [''],
    });

    this.formGroup.next(null);

    this.calendarEvents$.value.push({
      id: this.commonService.generateGuid(),
      subject: '',
      startTime: new Date(2023, 4, 1),
      endTime: new Date(2023, 4, 1, 1, 0),
      color: 'parent-class',
    });

    // this.SelectedAutomationTypeFormControl.valueChanges
    // .pipe(distinctUntilChanged())
    // .subscribe((value: any) => {
    //   if(this.actionsTableDataNew.length > 0)
    //   {
    //     this.clearAllActions();
    //   }
    // });
  }

  ngOnInit() {
    this.store.select(TENANTS_INFO).subscribe((tenant: TENANTSINTERFACE) => {
      this.tenantId = tenant.selected_tenant.id;
      this.tenantAccess = tenant.accessed_tenant;
      this.loggedInTenant = tenant.selected_tenant;
      this.tenants.push(this.tenantId);
    });

    this.companyInfoDetail$ = this.store.select(COMPANY_INFO);
    this.companyInfoDetail$.subscribe(
      (responseData: CompanyInfoStoreInterface) => {
        this.companyDetails.next(responseData.compDetails);
      }
    );

    this.route.params.subscribe((params) => {
      if (params['id'] != undefined && params['id'] != '') {
        this.automationGuid = params['id'];
        this.getAutomationMaster();
      } else {
        if (this.routeStateService.getAutomationData()) {
          this.automationData = this.routeStateService.getAutomationData()!;
          this.SelectedAutomationTypeFormControl.setValue(
            this.automationData.automationMaster!
          );
          this.AutomationNameFormControl.setValue(this.automationData.name);
          this.AutomationDescriptionFormControl.setValue(
            this.automationData.description
          );
          this.showSkeleton$.next(false);
        } else {
          let modelData: CustomAutomationData = {
            name: '',
            description: '',
            ownershiplevel: OwnershipLevel.Tenant,
            selectedCompanyTenants: [],
            selectedFrenchiseTenants: [],
            selectedFrenchise: null,
            companyId: 0,
            tenantId: 0,
            franchiseId: 0,
            associatedStaff: false,
            additionalStaffTo: [],
            automationType: null,
            sqlRule: '',
            jsonRule: '',
            sqlQuery: '',
            formGroup: new UntypedFormGroup({}),
            event: 'create',
            automationMaster: null,
            notificationSelected: false,
            staffNotificationForm: new UntypedFormGroup({}),
            actionsConfigured: false,
            dataChanged: false,
          };
          this.showSkeleton$.next(false);

          this.refAutomationModal = this.dialogService.open(
            CustomAutomationPopupComponent,
            {
              showHeader: false,
              width: '90vw',
              height: 'auto',
              baseZIndex: 10000,
              maximizable: false,
              modal: true,
              dismissableMask: false,
              closable: true,
              data: modelData,
            }
          );

          this.refAutomationModal.onClose.subscribe((res: any) => {
            if (res) {
              this.automationData = res;
              if (this.automationData.event == 'create') {
                this.refEventTrigeersModal = this.dialogService.open(
                  SelectAutomationEventPopupComponent,
                  {
                    showHeader: false,
                    width: '90vw',
                    height: '80vh',
                    baseZIndex: 10001,
                    maximizable: false,
                    modal: true,
                    dismissableMask: false,
                    closable: true,
                    data: this.automationData, // Make sure to pass the correct data if needed
                  }
                );

                // Handle the onClose event of the second dialog
                this.refEventTrigeersModal.onClose.subscribe(
                  (secondDialogCloseRes: any) => {
                    if (secondDialogCloseRes) {
                      this.automationData = secondDialogCloseRes;
                      this.SelectedAutomationTypeFormControl.setValue(
                        this.automationData.automationMaster!
                      );
                      this.AutomationNameFormControl.setValue(
                        this.automationData.name
                      );
                      this.AutomationDescriptionFormControl.setValue(
                        this.automationData.description
                      );
                      this.cd.detectChanges();
                    }
                  }
                );
              }
              this.cd.detectChanges();
            }
          });
        }
      }
    });

    //this.getAutomationMaster();

    // Update the event settings
    this.eventSettings = {
      dataSource: this.calendarEvents$.value,
      fields: {
        id: 'id',
        subject: { name: 'subject' },
        startTime: { name: 'startTime' },
        endTime: { name: 'endTime' }
      }
    };
  }

  SetOnLoadData(): void {
    this.showSkeleton$.next(true);
    this.automationService.GetAutomationByUuid(this.automationGuid).subscribe({
      next: (response: AutomationByIdResponse) => {
        this.automationResponse = response;
        const automationtype = this.automationMasters.find(
          (o) =>
            o.automation_type == response.campaign_event_type ||  
            o.automation_type.toString() == AutomationEventType[response.campaign_event_type])!.automation_type;
        const modelData: CustomAutomationData = {
          name: response.campaign_name,
          description: response.campaign_description,
          ownershiplevel: OwnershipLevel[
            response.ownership_level.toString() as keyof typeof OwnershipLevel
          ],
          selectedCompanyTenants: response.tenant_ids,
          selectedFrenchiseTenants:
            response.ownership_level == OwnershipLevel.Franchise ||
            response.ownership_level.toString() ==
              OwnershipLevel[OwnershipLevel.Franchise]
              ? response.tenant_ids
              : [],
          selectedFrenchise: null,
          companyId: response.company_id,
          tenantId: (response.tenant_ids && response.tenant_ids.find(o => o == this.tenantId)!) ? response.tenant_ids.find(o => o == this.tenantId)! : this.tenantId,
          franchiseId: response.franchise_id,
          associatedStaff:false,
          additionalStaffTo: [],
          automationType: automationtype,
          sqlRule: response.actions_group.source_data_extraction_rules.rule?.sql!,
          jsonRule: '',
          sqlQuery: '',
          formGroup: new UntypedFormGroup({}),
          event: 'update',
          automationMaster: this.automationMasters.find((o) =>
              o.automation_type == automationtype ||
              o.automation_type.toString() ==
                AutomationEventType[automationtype])!,
          notificationSelected: false,
          staffNotificationForm: new UntypedFormGroup({}),
          actionsConfigured: false,
          dataChanged: false,
        };
        this.automationData = modelData;
        this.routeStateService.setAutomationData(modelData)!;
        this.SelectedAutomationTypeFormControl.setValue(this.automationData.automationMaster!);
        this.AutomationNameFormControl.setValue(this.automationData.name);
        this.AutomationDescriptionFormControl.setValue(this.automationData.description);
        this.setActionData(this.automationData);
        this.showSkeleton$.next(false);
        this.cd.detectChanges();
      },
      error: (error: any) => {
        this.automationResponse = {} as AutomationByIdResponse;
        this.showSkeleton$.next(false);
        this.cd.detectChanges();
      },
    });
  }

  
  setActionData(automationData:CustomAutomationData) {
    this.automationResponse.actions_group.action_payload.forEach(action => {
      let scheduleActionPayload: ScheduleActionPayload = {
        schedule_in_min: action.schedule_in_min,
        action_name: "",
        associated_staff: false,
        description: "",
        staff_ids: [],
        actions: {} as Actions
      };
      let actions: Actions = {
        email_action_payload: null,
        sms_action_payload: null,
        whatsapp_action_payload: null,
        task_action_payload: null,
      };
      scheduleActionPayload.actions= actions;
  
      if (action.actions.client_email_action_payload) {
        scheduleActionPayload.actions.email_action_payload = action.actions.client_email_action_payload;
        this.addActionData(action.actions.client_email_action_payload, SendNotificationTo.Client, 'SEND_EMAIL', 'sendemail', this.commonService.generateGuid(), action.schedule_in_min, scheduleActionPayload,automationData.automationType?.toString()!);
      }
  
      if (action.actions.staff_email_action_payload) {
        scheduleActionPayload.actions.email_action_payload = action.actions.staff_email_action_payload;
        this.automationData.notificationSelected = action.actions.notification_staff ?  action.actions.notification_staff.notify_record_associated_staff : false;
        this.automationData.additionalStaffTo = action.actions.notification_staff ?  action.actions.notification_staff.notify_additional_staff_to : [];
        this.automationData.associatedStaff = action.actions.notification_staff ?  action.actions.notification_staff.notify_record_associated_staff : false;
        this.addActionData(action.actions.staff_email_action_payload, SendNotificationTo.Staff, 'SEND_EMAIL', 'sendemail', this.commonService.generateGuid(), action.schedule_in_min, scheduleActionPayload,automationData.automationType?.toString()!);
      }
  
      if (action.actions.client_sms_action_payload) {
        scheduleActionPayload.actions.sms_action_payload = action.actions.client_sms_action_payload;
        this.addActionData(action.actions.client_sms_action_payload, SendNotificationTo.Client, 'SEND_SMS', 'sendsms', this.commonService.generateGuid(), action.schedule_in_min, scheduleActionPayload,automationData.automationType?.toString()!);
      }
  
      if (action.actions.staff_sms_action_payload) {
        scheduleActionPayload.actions.sms_action_payload = action.actions.staff_sms_action_payload;
        this.automationData.notificationSelected = action.actions.notification_staff ?  action.actions.notification_staff.notify_record_associated_staff : false;
        this.automationData.additionalStaffTo = action.actions.notification_staff ?  action.actions.notification_staff.notify_additional_staff_to : [];
        this.automationData.associatedStaff = action.actions.notification_staff ?  action.actions.notification_staff.notify_record_associated_staff : false;
        this.addActionData(action.actions.staff_sms_action_payload, SendNotificationTo.Staff, 'SEND_SMS', 'sendsms', this.commonService.generateGuid(), action.schedule_in_min, scheduleActionPayload,automationData.automationType?.toString()!);
      }
  
      if (action.actions.client_whatsapp_action_payload) {
        scheduleActionPayload.actions.whatsapp_action_payload = action.actions.client_whatsapp_action_payload;
        this.addActionData(action.actions.client_whatsapp_action_payload, SendNotificationTo.Client, 'SEND_WHATSAPP', 'sendwhatsapp', this.commonService.generateGuid(), action.schedule_in_min, scheduleActionPayload,automationData.automationType?.toString()!);
      }
  
      if (action.actions.staff_whatsapp_action_payload) {
        scheduleActionPayload.actions.whatsapp_action_payload = action.actions.staff_whatsapp_action_payload;
        this.automationData.notificationSelected = action.actions.notification_staff ?  action.actions.notification_staff.notify_record_associated_staff : false;
        this.automationData.additionalStaffTo = action.actions.notification_staff ?  action.actions.notification_staff.notify_additional_staff_to : [];
        this.automationData.associatedStaff = action.actions.notification_staff ?  action.actions.notification_staff.notify_record_associated_staff : false;
        this.addActionData(action.actions.staff_whatsapp_action_payload, SendNotificationTo.Staff, 'SEND_WHATSAPP', 'sendwhatsapp', this.commonService.generateGuid(), action.schedule_in_min, scheduleActionPayload,automationData.automationType?.toString()!);
      }
  
      if (action.actions.staff_task_action_payload) {
        scheduleActionPayload.actions.task_action_payload = action.actions.staff_task_action_payload;
        this.automationData.notificationSelected = action.actions.notification_staff ?  action.actions.notification_staff.notify_record_associated_staff : false;
        this.automationData.additionalStaffTo = action.actions.notification_staff ?  action.actions.notification_staff.notify_additional_staff_to : [];
        this.automationData.associatedStaff = action.actions.notification_staff ?  action.actions.notification_staff.notify_record_associated_staff : false;
        this.addActionData(action.actions.staff_task_action_payload, SendNotificationTo.Staff, 'CREATE_TASK', 'createtask', this.commonService.generateGuid(), action.schedule_in_min, scheduleActionPayload, automationData.automationType?.toString()!);
      }
    });
    
  }
  
  addActionData(actionPayload: any, notificationTo: SendNotificationTo, actionText: string, actionKey: string, uuid: string, scheduleInMin: number, scheduleActionPayload: ScheduleActionPayload, automationType:string) {
    let icon: string;
    let color: string;
  
    switch (actionKey) {
      case 'sendemail':
        icon = 'fa fa-envelope font-light';
        color = 'cyan-500';
        break;
      case 'sendsms':
        icon = 'fa fa-comments';
        color = 'pink-500';
        break;
      case 'sendwhatsapp':
        icon = 'fab fa-whatsapp';
        color = 'green-500';
        break;
      case 'createtask':
        icon = 'fa fa-phone';
        color = 'orange-500';
        break;
      default:
        icon = '';
        color = '';
        break;
    }
  
    this.actionsTableDataNew.push({
      text: actionText,
      key: actionKey,
      icon: icon,
      color: color,
      status: "",
      configured: true,
      schedule_event: this.formatScheduleTime(scheduleInMin, automationType),
      uuid: uuid,
      action_payload: actionPayload,
      schedule_in_min: scheduleInMin,
      formgroup: null,
      notificationTo: notificationTo,
      action_data: scheduleActionPayload,
      description: actionKey == 'sendemail' ? actionPayload.subject || '' : actionKey == 'sendsms' ? actionPayload.message || '' : actionKey == 'sendwhatsapp' ? actionPayload.message || '' : actionKey == 'createtask' ? actionPayload.title || '' : ""
    });
  
    this.automationData.actionsConfigured = true;
  }
  

  getAutomationMaster() {
    this.automationService.GetAutomationMasters().subscribe({
      next: (response: AutomationMaster[]) => {
        this.automationMasters =
          response && response.length > 0 ? response : [];
        this.SetOnLoadData();
      },
      error: (error: any) => {
        this.automationMasters = [];
      },
    });
  }

  calculateTomorrowDate() {
    const today = new Date().setHours(0, 0, 0, 0);
    let tomorrowDate = new Date(today);
    return new Date(tomorrowDate.setDate(tomorrowDate.getDate() + 1));
  }

  OpenCustomAutomationPopup() {
    this.refAutomationModal = this.dialogService.open(
      CustomAutomationPopupComponent,
      {
        showHeader: false,
        width: '90vw',
        height: 'auto',
        baseZIndex: 10000,
        maximizable: false,
        modal: true,
        dismissableMask: false,
        closable: true,
        data: this.automationData,
      }
    );

    this.refAutomationModal.onClose.subscribe((res: any) => {
      if (res) {
        this.automationData = res;
        this.AutomationNameFormControl.setValue(
          this.automationData.name
        );
        this.AutomationDescriptionFormControl.setValue(
          this.automationData.description
        );
        if (this.automationData.dataChanged) this.clearAllActions();
        this.cd.detectChanges();
      }
    });
  }

  OpenEventPopup() {
    this.refEventTrigeersModal = this.dialogService.open(
      SelectAutomationEventPopupComponent,
      {
        showHeader: false,
        width: '90vw',
        height: '80vh',
        baseZIndex: 10001,
        maximizable: false,
        modal: true,
        dismissableMask: false,
        closable: true,
        data: this.automationData, // Make sure to pass the correct data if needed
      }
    );

    this.refEventTrigeersModal.onClose.subscribe(
      (secondDialogCloseRes: any) => {
        if (secondDialogCloseRes) {
          this.automationData = secondDialogCloseRes;
          this.SelectedAutomationTypeFormControl.setValue(
            this.automationData!.automationMaster
          );
          if (this.automationData.dataChanged) this.clearAllActions();
          this.cd.detectChanges();
        }
      }
    );
  }

  OpenQueryBuilderPopup() {
    this.refQueryBuilderModal = this.dialogService.open(
      RuleBuilderPopupComponent,
      {
        showHeader: false,
        width: '90vw',
        baseZIndex: 10000,
        styleClass: 'no-header-modal',
        data: this.automationData, // Make sure to pass the correct data if needed
      }
    );

    this.refQueryBuilderModal.onClose.subscribe((res: any) => {
      if (res) {
        this.automationData = res;
        this.RuleFormControl.setValue(this.automationData?.sqlRule);
        this.cd.detectChanges();
      }
    });
  }

  OpenStaffNotificationPopup() {
    this.showStaffNotificationSettings = true;
  }

  HideStaffNotificationPopup(event: {
    closePopup: boolean;
    data: CustomAutomationData;
    showAnotherPopup: boolean;
  }) {
    this.automationData = event.data;
    this.showStaffNotificationSettings = false;
    if (event.showAnotherPopup) {
      this.configureActions(
        this.actionSelectionData.action,
        this.actionSelectionData.notificationTo,
        this.actionSelectionData.data,
        this.actionSelectionData.mode
      );
    }
    this.cd.detectChanges();
  }

  getRule(event: any) {
    this.RuleFormControl.setValue(event);
  }

  configureActions(
    action: string,
    notificationTo: SendNotificationTo,
    data: any,
    mode: string = 'create'
  ) {
    this.mode=mode;
    this.selectedNotificationTo = notificationTo;
    this.uuid = mode === 'update' ? data.uuid : '';
    this.formGroup.next(mode === 'update' ? data.formgroup : null);
    if (
      notificationTo == SendNotificationTo.Staff &&
      !this.automationData?.notificationSelected
    ) {
      this.actionSelectionData = {
        action: action,
        notificationTo: notificationTo,
        data: data,
        mode: mode,
      };
      this.showStaffNotificationSettings = true;
      this.firstTimeSelected = true;
    } else {
      switch (action) {
        case 'sendemail':
          this.emailActionPayload = mode === 'update' ? data.action_payload : null;
          this.scheduleInMin= mode === 'update' ? data.schedule_in_min : 0;
          this.showBulkEmailPopup = true;
          break;
        case 'sendsms':
          this.smsActionPayload =
            mode === 'update' ? data.action_payload : null;
            this.scheduleInMin= mode === 'update' ? data.schedule_in_min : 0;
          this.showBulkSmsPopup = true;
          break;
        case 'sendwhatsapp':
          this.whatsAppActionPayload = mode === 'update' ? data.action_payload : null;
          this.scheduleInMin= mode === 'update' ? data.schedule_in_min : 0;
          this.showBulkWhatsAppPopup = true;
          break;
        case 'createtask':
          this.taskActionPayload =mode === 'update' ? data.action_payload : null;
          this.taskCreateType = TaskCreateType.Automation;
          this.scheduleInMin= mode === 'update' ? data.schedule_in_min : 0;
          this.showTaskCreate = true;
          break;
        case 'createopportunity':
          this.scheduleInMin= mode === 'update' ? data.schedule_in_min : 0;
          this.showCreateOpportunityPopup = true;
          break;
      }
      this.firstTimeSelected = false;
    }
  }

  deleteData(key: string, uuid: string) {
    // Create a copy of the event array
    const events = [...this.calendarEvents$.value];
    // Find the index of the event to be deleted
    const index = events.indexOf(
      events.filter((event) => event.id === uuid)[0]
    );
    // Remove the event from the array
    events.splice(index, 1);
    // Pass the new array to the calendar
    this.calendarEvents$.next(events);

    const tableData = [...this.actionsTableDataNew];
    // Find the index of the event to be deleted
    const tableIndex = tableData.indexOf(
      tableData.filter((event) => event.uuid === uuid)[0]
    );
    // Remove the event from the array
    tableData.splice(tableIndex, 1);
    // Pass the new array to the calendar
    this.actionsTableDataNew = tableData;
    if (this.actionsTableDataNew.length == 0)
      this.automationData.actionsConfigured = true;
    this.cd.detectChanges();
  }

  clearAllActions() {
    this.clearbtnLoading = true;
    this.actionsTableDataNew = [];
    this.calendarEvents$.next([]);
    this.calendarEvents$.value.push({
      id: this.commonService.generateGuid(),
      subject: '',
      startTime: new Date(2023, 4, 1),
      endTime: new Date(2023, 4, 1, 1, 0),
      color: 'parent-class',
    });
    this.automationData.actionsConfigured = false;
    this.clearbtnLoading = false;
  }

  getFirstDayOfWeek(d: Date, prev: boolean): Date {
    const day = d.getDay();
    const diff = d.getDate() - day + (prev ? -7 : 7);
    return new Date(d.setDate(diff));
  }

  navigatePage(prev: boolean): void {
    const currentDate = this.calenderDefaultdata;
    if (this.view === 'calendar') {
      const prevNextPage = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() + (prev ? -1 : 1),
        1
      );
      this.currentDate = prevNextPage;
      this.calenderDefaultdata = this.currentDate;
    } else {
      const prevNextSunday = this.getFirstDayOfWeek(currentDate, prev);
      this.currentDate = prevNextSunday;
      this.calenderDefaultdata = this.currentDate;
    }
  }

  hideOpportunity(e: any) {
    this.showCreateOpportunityPopup = false;
  }

  HideTaskCreate(e: any) {
    this.showTaskCreate = false;
  }

  HideEmailPopup(event: {
    closePopup: boolean;
    data: any | null;
    form: UntypedFormGroup | null;
    uuid: string;
    notificationTo: SendNotificationTo;
  }) {
    if (event.data) {
      this.formGroup.next(event.form as UntypedFormGroup);
      const data = event.data as ScheduleActionPayload;
      if (event.uuid !== '') {
        this.actionsTableDataNew.forEach((o) => {
          if (
            o.uuid == event.uuid &&
            o.notificationTo == event.notificationTo
          ) {
            o.action_payload = data.actions.email_action_payload;
            o.schedule_event = data.action_name;
            o.schedule_in_min = data.schedule_in_min;
            o.formgroup = event.form as UntypedFormGroup;
            o.action_data = data;
          }
        });

        const events = this.calendarEvents$.value;
        events.forEach((o) => {
          if (o.id == event.uuid) {
            o.startTime = this.calculateDateFromMinutes(data.schedule_in_min);
            o.endTime = new Date(o.startTime.getTime() + 60 * 60 * 1000); // Add 1 hour
          }
        });
        this.calendarEvents$.next(events);
      } else {
        const uuid = this.commonService.generateGuid();
        this.actionsTableDataNew.push({
          text: 'SEND_EMAIL',
          key: 'sendemail',
          icon: 'fa fa-envelope font-light',
          color: 'cyan-500',
          status: '',
          configured: true,
          schedule_event: data.action_name,
          uuid: uuid,
          action_payload: data.actions.email_action_payload,
          schedule_in_min: data.schedule_in_min,
          formgroup: event.form as UntypedFormGroup,
          notificationTo: event.notificationTo,
          action_data: data,
          description: data.description,
        });
        this.automationData.actionsConfigured = true;
        
        // Create calendar event in Syncfusion format
        const startTime = this.calculateDateFromMinutes(data.schedule_in_min);
        const endTime = new Date(startTime.getTime() + 60 * 60 * 1000); // Add 1 hour
        let subject = '';
        
        this.translateService
          .get(['CAMPAIGN.TABLE.SEND_EMAIL_CLIENT', 'CAMPAIGN.TABLE.SEND_EMAIL_STAFF'])
          .subscribe((translation) => {
            subject = event.notificationTo == SendNotificationTo.Client
              ? translation['CAMPAIGN.TABLE.SEND_EMAIL_CLIENT']
              : translation['CAMPAIGN.TABLE.SEND_EMAIL_STAFF'];
        });
        
        const calendarEvent: CalendarEventModel = {
          id: uuid,
          subject: subject,
          startTime: startTime,
          endTime: endTime,
          color: '#06b6d4',
          text: 'sendemail'
        };
        
        const events = this.calendarEvents$.value;
        events.push(calendarEvent);
        this.calendarEvents$.next(events);
        
        // Update event settings with the new data
        this.eventSettings = {
          ...this.eventSettings,
          dataSource: this.calendarEvents$.value
        };
        this.scheduleObj?.setProperties({ eventSettings: this.eventSettings });
      }

      this.cd.detectChanges();
    }
    this.showBulkEmailPopup = false;
  }

  HideSMSPopup(event: {
    closePopup: boolean;
    data: any | null;
    form: UntypedFormGroup | null;
    uuid: string;
    notificationTo: SendNotificationTo;
  }) {
    this.showBulkSmsPopup = false;
    if (event.data) {
      this.formGroup.next(event.form as UntypedFormGroup);
      const data = event.data as ScheduleActionPayload;
      if (event.uuid !== '') {
        this.actionsTableDataNew.forEach((o) => {
          if (
            o.uuid == event.uuid &&
            o.notificationTo == event.notificationTo
          ) {
            o.action_payload = data.actions.sms_action_payload;
            o.schedule_event = data.action_name;
            o.schedule_in_min = data.schedule_in_min;
            o.formgroup = event.form as UntypedFormGroup;
            o.action_data = data;
          }
        });
        const events = this.calendarEvents$.value;
        events.forEach((o) => {
          if (o.id == event.uuid) {
            o.startTime = this.calculateDateFromMinutes(data.schedule_in_min);
            o.endTime = new Date(o.startTime.getTime() + 60 * 60 * 1000); // Add 1 hour
          }
        });
        this.calendarEvents$.next(events);
      } else {
        const uuid = this.commonService.generateGuid();
        this.actionsTableDataNew.push({
          text: 'SEND_SMS',
          key: 'sendsms',
          icon: 'fa fa-comments',
          color: 'pink-500',
          status: data.description,
          configured: true,
          schedule_event: data.action_name,
          uuid: uuid,
          action_payload: data.actions.sms_action_payload,
          schedule_in_min: data.schedule_in_min,
          formgroup: event.form as UntypedFormGroup,
          notificationTo: event.notificationTo,
          action_data: data,
          description: data.description,
        });
        this.automationData.actionsConfigured = true;
        
        // Create calendar event in Syncfusion format
        const startTime = this.calculateDateFromMinutes(data.schedule_in_min);
        const endTime = new Date(startTime.getTime() + 60 * 60 * 1000); // Add 1 hour
        let subject = '';
        
        this.translateService
          .get([
            'CAMPAIGN.TABLE.SEND_SMS_CLIENT',
            'CAMPAIGN.TABLE.SEND_SMS_STAFF',
          ])
          .subscribe((translation) => {
            subject = event.notificationTo == SendNotificationTo.Client
              ? translation['CAMPAIGN.TABLE.SEND_SMS_CLIENT']
              : translation['CAMPAIGN.TABLE.SEND_SMS_STAFF'];
        });
        
        const calendarEvent: CalendarEventModel = {
          id: uuid,
          subject: subject,
          startTime: startTime,
          endTime: endTime,
          color: '#ec4899',
          text: 'sendsms'
        };
        
        const events = this.calendarEvents$.value;
        events.push(calendarEvent);
        this.calendarEvents$.next(events);
        
        // Update event settings with the new data
        this.eventSettings = {
          ...this.eventSettings,
          dataSource: this.calendarEvents$.value
        };
        this.scheduleObj?.setProperties({ eventSettings: this.eventSettings });
      }
      this.cd.detectChanges();
    }
  }

  HideWhatsAppPopup(event: {
    closePopup: boolean;
    data: any | null;
    form: UntypedFormGroup | null;
    uuid: string;
    notificationTo: SendNotificationTo;
  }) {
    this.showBulkWhatsAppPopup = false;
    if (event.data) {
      this.formGroup.next(event.form as UntypedFormGroup);
      const data = event.data as ScheduleActionPayload;
      if (event.uuid !== '') {
        this.actionsTableDataNew.forEach((o) => {
          if (
            o.uuid == event.uuid &&
            o.notificationTo == event.notificationTo
          ) {
            o.action_payload = data.actions.whatsapp_action_payload;
            o.schedule_event = data.action_name;
            o.schedule_in_min = data.schedule_in_min;
            o.formgroup = event.form as UntypedFormGroup;
            o.action_data = data;
          }
        });
        const events = this.calendarEvents$.value;
        events.forEach((o) => {
          if (o.id == event.uuid) {
            o.startTime = this.calculateDateFromMinutes(data.schedule_in_min);
            o.endTime = new Date(o.startTime.getTime() + 60 * 60 * 1000); // Add 1 hour
          }
        });
        this.calendarEvents$.next(events);
      } else {
        const uuid = this.commonService.generateGuid();
        this.actionsTableDataNew.push({
          text: 'SEND_WHATSAPP',
          key: 'sendwhatsapp',
          icon: 'fab fa-whatsapp',
          color: 'green-500',
          status: data.description,
          configured: true,
          schedule_event: data.action_name,
          uuid: uuid,
          action_payload: data.actions.whatsapp_action_payload,
          schedule_in_min: data.schedule_in_min,
          formgroup: event.form as UntypedFormGroup,
          notificationTo: event.notificationTo,
          action_data: data,
          description: data.description,
        });
        this.automationData.actionsConfigured = true;
        
        // Create calendar event in Syncfusion format
        const startTime = this.calculateDateFromMinutes(data.schedule_in_min);
        const endTime = new Date(startTime.getTime() + 60 * 60 * 1000); // Add 1 hour
        let subject = '';
        
        this.translateService
          .get([
            'CAMPAIGN.TABLE.SEND_WHATSAPP_CLIENT',
            'CAMPAIGN.TABLE.SEND_WHATSAPP_STAFF',
          ])
          .subscribe((translation) => {
            subject = event.notificationTo == SendNotificationTo.Client
              ? translation['CAMPAIGN.TABLE.SEND_WHATSAPP_CLIENT']
              : translation['CAMPAIGN.TABLE.SEND_WHATSAPP_STAFF'];
        });
        
        const calendarEvent: CalendarEventModel = {
          id: uuid,
          subject: subject,
          startTime: startTime,
          endTime: endTime,
          color: '#22c55e',
          text: 'sendwhatsapp'
        };
        
        const events = this.calendarEvents$.value;
        events.push(calendarEvent);
        this.calendarEvents$.next(events);
        
        // Update event settings with the new data
        this.eventSettings = {
          ...this.eventSettings,
          dataSource: this.calendarEvents$.value
        };
        this.scheduleObj?.setProperties({ eventSettings: this.eventSettings });
      }
      this.cd.detectChanges();
    }
  }

  hideTaskCreate(event: {
    closePopup: boolean;
    data: any | null;
    form: UntypedFormGroup | null;
    uuid: string;
    notificationTo: SendNotificationTo;
  }) {
    if (event.data) {
      this.formGroup.next(event.form as UntypedFormGroup);
      const data = event.data as ScheduleActionPayload;
      if (event.uuid !== '') {
        this.actionsTableDataNew.forEach((o) => {
          if (
            o.uuid == event.uuid &&
            o.notificationTo == event.notificationTo
          ) {
            o.action_payload = data.actions.task_action_payload;
            o.schedule_event = data.action_name;
            o.schedule_in_min = data.schedule_in_min;
            o.formgroup = event.form as UntypedFormGroup;
            o.action_data = data;
          }
        });
        const events = this.calendarEvents$.value;
        events.forEach((o) => {
          if (o.id == event.uuid) {
            o.startTime = this.calculateDateFromMinutes(data.schedule_in_min);
            o.endTime = new Date(o.startTime.getTime() + 60 * 60 * 1000); // Add 1 hour
          }
        });
        this.calendarEvents$.next(events);
      } else {
        const uuid = this.commonService.generateGuid();
        this.actionsTableDataNew.push({
          text: 'CREATE_TASK',
          key: 'createtask',
          icon: 'fa fa-phone',
          color: 'orange-500',
          status: '',
          configured: true,
          schedule_event: data.action_name,
          uuid: uuid,
          action_payload: data.actions.task_action_payload,
          schedule_in_min: data.schedule_in_min,
          formgroup: event.form as UntypedFormGroup,
          notificationTo: event.notificationTo,
          action_data: data,
          description: data.description,
        });
        this.automationData.actionsConfigured = true;
        
        // Create calendar event in Syncfusion format
        const startTime = this.calculateDateFromMinutes(data.schedule_in_min);
        const endTime = new Date(startTime.getTime() + 60 * 60 * 1000); // Add 1 hour
        let subject = '';
        
        this.translateService
          .get([
            'CAMPAIGN.TABLE.CREATE_TASK_CLIENT',
            'CAMPAIGN.TABLE.CREATE_TASK_STAFF',
          ])
          .subscribe((translation) => {
            subject = event.notificationTo == SendNotificationTo.Client
              ? translation['CAMPAIGN.TABLE.CREATE_TASK_CLIENT']
              : translation['CAMPAIGN.TABLE.CREATE_TASK_STAFF'];
        });
        
        const calendarEvent: CalendarEventModel = {
          id: uuid,
          subject: subject,
          startTime: startTime,
          endTime: endTime,
          color: '#f97316',
          text: 'createtask'
        };
        
        const events = this.calendarEvents$.value;
        events.push(calendarEvent);
        this.calendarEvents$.next(events);
        
        // Update event settings with the new data
        this.eventSettings = {
          ...this.eventSettings,
          dataSource: this.calendarEvents$.value
        };
        this.scheduleObj?.setProperties({ eventSettings: this.eventSettings });
      }
      this.cd.detectChanges();
    }
    this.showTaskCreate = false;
  }

  calculateDateFromMinutes(minutes: number): Date {
    const currentDate = new Date(2023, 4, 1);
    if (minutes > 0) {
      const targetDate = new Date(currentDate.getTime() + minutes * 60 * 1000);
      return targetDate;
    } else {
      return currentDate;
    }
  }

  CreateAutomation() {
    if (this.createAutomationForm.valid) {
      if (!this.isActionSelected()) {
        this.translateService
          .get('CAMPAIGN.WARNING_MSG')
          .subscribe((translation) => {
            this.toastService.showWarning(translation);
          });
        this.cd.detectChanges();
        return;
      }
      this.btnLoading = true;
     
        if(this.automationGuid != "")
        {
          let payloadRequest: UpdateAutomation = this.getUpdateAutomationObject();
          this.campaignsService.UpdateAutomation(this.automationGuid,payloadRequest).subscribe({
            next: async (res) => {
              this.translateService
                .get('AUTOMATION.CREATE.UPDATE_MSG')
                .subscribe((translation) => {
                  this.toastService.showSuccess(translation);
                });
              this.btnLoading = false;
              this.createAutomationForm.reset();
              this.cd.detectChanges();
              await this.timeout(100);
              this.router.navigateByUrl('/automation');
            },
            error: async (error) => {
              if (error.originalError.status == 200) {
                this.translateService
                  .get('AUTOMATION.CREATE.UPDATE_MSG')
                  .subscribe((translation) => {
                    this.toastService.showSuccess(translation);
                  });
                this.btnLoading = false;
                this.createAutomationForm.reset();
                this.cd.detectChanges();
                await this.timeout(100);
                this.router.navigateByUrl('/automation');
              } else {
                this.toastService.showError(error);
                this.btnLoading = false;
                this.cd.detectChanges();
              }
            },
          });
        }
        else{
          let payloadRequest: CreateAutomation[] = this.getCreateAutomationObject();
          this.campaignsService.CreateAutomation(payloadRequest).subscribe({
            next: async (res) => {
              this.translateService
                .get('AUTOMATION.CREATE.MSG')
                .subscribe((translation) => {
                  this.toastService.showSuccess(translation);
                });
              this.btnLoading = false;
              this.createAutomationForm.reset();
              this.cd.detectChanges();
              await this.timeout(100);
              this.router.navigateByUrl('/automation');
            },
            error: async (error) => {
              if (error.originalError.status == 200) {
                this.translateService
                  .get('AUTOMATION.CREATE.MSG')
                  .subscribe((translation) => {
                    this.toastService.showSuccess(translation);
                  });
                this.btnLoading = false;
                this.createAutomationForm.reset();
                this.cd.detectChanges();
                await this.timeout(100);
                this.router.navigateByUrl('/automation');
              } else {
                this.toastService.showError(error);
                this.btnLoading = false;
                this.cd.detectChanges();
              }
            },
          });
        }
    } else {
      Object.keys(this.createAutomationForm.controls).forEach((field) => {
        const control = this.createAutomationForm.get(field);
        control?.markAsTouched({ onlySelf: true });
      });
      const firstElementWithError: Element | null =
        document.querySelector('.ng-invalid');
      if (firstElementWithError)
        firstElementWithError.scrollIntoView({ behavior: 'smooth' });
      this.btnLoading = false;
      this.cd.detectChanges();
    }
  }

  getCreateAutomationObject(): CreateAutomation[] {
    let payloadRequest: CreateAutomation[] = [] as CreateAutomation[];
    const formValues = this.createAutomationForm.value;

    let tenants: number[] = [];
    switch (this.automationData.ownershiplevel) {
      case OwnershipLevel.Company:
        tenants = this.automationData.selectedCompanyTenants;
        break;
      case OwnershipLevel.Franchise:
        tenants = this.automationData.selectedFrenchiseTenants;
        break;
      case OwnershipLevel.Tenant:
        tenants = this.tenants;
        break;
      default:
        break;
    }
    tenants.forEach((o: any) => {
      let payload: CreateAutomation = {} as CreateAutomation;
      payload.tenant_id = o;
      payload.automation_name = formValues['automation_name'];
      payload.automation_description = formValues['automation_description'];
      payload.actions_group = {} as AutomationActionsGroup;
      payload.automation_ownership = this.automationData.ownershiplevel;
      payload.automation_franchise = this.automationData.ownershiplevel == OwnershipLevel.Franchise
          ? this.automationData.selectedFrenchise
            ? this.automationData.selectedFrenchise?.franchise_group_id
            : 0
          : 0;
      let actionGroups: AutomationActionsGroup = {} as AutomationActionsGroup;
      // actionGroups.campaign_action_date = this.timeService.ConvertLocalDateTimeToUTC(formValues["campaign_action_date"]);
      actionGroups.source_data_type = CampaignSource.Rules;
      let sourceDataExtractionRules: SourceDataExtractionRules = {} as SourceDataExtractionRules;
      let rule: Rule = {} as Rule;
      let params: Params = {} as Params;
      rule.params = params;
      let jsonString: string = formValues['rule'];

      if (jsonString && jsonString != '') {
        const jsonObject = JSON.parse(jsonString);
        rule.params = jsonObject.params;
        rule.sql = jsonObject.sql;
      }

      sourceDataExtractionRules.clients = null;
      sourceDataExtractionRules.reports = null;
      sourceDataExtractionRules.rule = Object.keys(rule.params).length > 0 ? rule : null;
      sourceDataExtractionRules.jsonrule = Object.keys(rule.params).length > 0 ? rule : null;
      actionGroups.source_data_extraction_rules = sourceDataExtractionRules;
      actionGroups.action_payload = [] as AutomationActionPayload[];

      this.actionsTableDataNew.forEach((action) => {
        let actionPayloads: AutomationActionPayload = {} as AutomationActionPayload;
        actionPayloads.actions = {} as AutomationActions;
        let payloadKey: string = '';
        if (action.key == 'sendemail')
          payloadKey = action.notificationTo === SendNotificationTo.Client
              ? `client_email_action_payload`
              : `staff_email_action_payload`;
        if (action.key == 'sendsms')
          payloadKey = action.notificationTo === SendNotificationTo.Client
              ? `client_sms_action_payload`
              : `staff_sms_action_payload`;
        if (action.key == 'sendwhatsapp')
          payloadKey =
            action.notificationTo === SendNotificationTo.Client
              ? `client_whatsapp_action_payload`
              : `staff_whatsapp_action_payload`;
        if (action.key == 'createtask')
          payloadKey = `staff_task_action_payload`;

        actionPayloads.actions[payloadKey as keyof AutomationActions] =
          action.action_payload;
        let notificationStaff: NotificationToStaff = {} as NotificationToStaff;
        notificationStaff.notify_record_associated_staff = action.notificationTo == SendNotificationTo.Staff
            ? this.automationData.associatedStaff
            : false;
        notificationStaff.notify_additional_staff_to = action.notificationTo == SendNotificationTo.Staff
            ? this.automationData.additionalStaffTo
            : [];
        actionPayloads.actions['notification_staff'] = notificationStaff;
        actionPayloads.schedule_in_min = action.schedule_in_min;
        actionGroups.action_payload.push(actionPayloads);
      });

      const mergedData: any[] = [];
      const existingData: any = actionGroups.action_payload;

      existingData.forEach((item: any) => {
        const existingItem = mergedData.find(
          (el) => el.schedule_in_min === item.schedule_in_min
        );
        if (existingItem) {
          existingItem.actions = {
            ...existingItem.actions,
            ...item.actions,
          };
        } else {
          mergedData.push({ ...item });
        }
      });
      const allKeys = [
        'client_email_action_payload',
        'client_sms_action_payload',
        'client_whatsapp_action_payload',
        'staff_email_action_payload',
        'staff_sms_action_payload',
        'staff_whatsapp_action_payload',
        'staff_task_action_payload',
        'client_pushnotification_action_payload',
        'staff_pushnotification_action_payload',
      ];
      mergedData.forEach((item) => {
        allKeys.forEach((key) => {
          if (!item.actions[key]) {
            item.actions[key] = null;
          }
        });
      });
      actionGroups.action_payload = mergedData;
      payload.actions_group = actionGroups;
      payload.campaign_event_type = this.SelectedAutomationTypeFormControl.value.automation_type;
      payloadRequest.push(payload);
      console.log('payload =>', payloadRequest);
    });
    return payloadRequest;
  }

  getUpdateAutomationObject(): UpdateAutomation {
    let payloadRequest: UpdateAutomation = {} as UpdateAutomation;
    const formValues = this.createAutomationForm.value;

    let tenants: number[] = [];
    switch (this.automationData.ownershiplevel) {
      case OwnershipLevel.Company:
        tenants = this.automationData.selectedCompanyTenants;
        break;
      case OwnershipLevel.Franchise:
        tenants = this.automationData.selectedFrenchiseTenants;
        break;
      case OwnershipLevel.Tenant:
        tenants = this.tenants;
        break;
      default:
        tenants = this.tenants;
        break;
    }

    payloadRequest.tenant_ids= tenants;
    payloadRequest.campaign_name = formValues['automation_name'];
    payloadRequest.campaign_description = formValues['automation_description'];
    payloadRequest.actions_group = {} as ActionsGroup;
    let notificationStaff: NotificationToStaff ={} as NotificationToStaff;

    let actionGroups: ActionsGroup = {} as ActionsGroup;
    actionGroups.source_data_type = CampaignSource.Rules;
      let sourceDataExtractionRules: SourceDataExtractionRules = {} as SourceDataExtractionRules;
      let rule: Rule = {} as Rule;
      let params: Params = {} as Params;
      rule.params = params;
      let jsonString: string = formValues['rule'];

      if (jsonString && jsonString != '') {
        const jsonObject = JSON.parse(jsonString);
        rule.params = jsonObject.params;
        rule.sql = jsonObject.sql;
      }

      sourceDataExtractionRules.clients = null;
      sourceDataExtractionRules.reports = null;
      sourceDataExtractionRules.rule = Object.keys(rule.params).length > 0 ? rule : null;
      sourceDataExtractionRules.jsonrule = Object.keys(rule.params).length > 0 ? rule : null;
      actionGroups.source_data_extraction_rules = sourceDataExtractionRules;
      actionGroups.source_data_type = CampaignSource.Rules;
      actionGroups.action_payload = [] as AutomationActionPayload[];
      this.actionsTableDataNew.forEach((action) => {
      let actionPayloads: AutomationActionPayload ={} as AutomationActionPayload;
      actionPayloads.actions = {} as AutomationActions;
      let payloadKey: string = '';
      if (action.key == 'sendemail')
        payloadKey =
          action.notificationTo === SendNotificationTo.Client
            ? `client_email_action_payload`
            : `staff_email_action_payload`;
      if (action.key == 'sendsms')
        payloadKey =
          action.notificationTo === SendNotificationTo.Client
            ? `client_sms_action_payload`
            : `staff_sms_action_payload`;
      if (action.key == 'sendwhatsapp')
        payloadKey =
          action.notificationTo === SendNotificationTo.Client
            ? `client_whatsapp_action_payload`
            : `staff_whatsapp_action_payload`;
      if (action.key == 'createtask')
        payloadKey = `staff_task_action_payload`;

      actionPayloads.actions[payloadKey as keyof AutomationActions] = action.action_payload;
      var notificationStaff: NotificationToStaff = {} as NotificationToStaff;
      notificationStaff.notify_record_associated_staff =
        action.notificationTo == SendNotificationTo.Staff
          ? this.automationData.associatedStaff
          : false;
      notificationStaff.notify_additional_staff_to =
        action.notificationTo == SendNotificationTo.Staff
          ? this.automationData.additionalStaffTo
          : [];
      actionPayloads.actions['notification_staff'] = notificationStaff;
      actionPayloads.schedule_in_min = action.schedule_in_min;
      actionGroups.action_payload.push(actionPayloads);
      });
    const mergedData: any[] = [];
    const existingData: any = actionGroups.action_payload;

    existingData.forEach((item: any) => {
      const existingItem = mergedData.find(
        (el) => el.schedule_in_min === item.schedule_in_min
      );
      if (existingItem) {
        existingItem.actions = {
          ...existingItem.actions,
          ...item.actions,
        };
      } else {
        mergedData.push({ ...item });
      }
    });
    const allKeys = [
      'client_email_action_payload',
      'client_sms_action_payload',
      'client_whatsapp_action_payload',
      'staff_email_action_payload',
      'staff_sms_action_payload',
      'staff_whatsapp_action_payload',
      'staff_task_action_payload',
      'client_pushnotification_action_payload',
      'staff_pushnotification_action_payload',
    ];
    mergedData.forEach((item) => {
      allKeys.forEach((key) => {
        if (!item.actions[key]) {
          item.actions[key] = null;
        }
      });
    });
    actionGroups.action_payload = mergedData;
    payloadRequest.actions_group = actionGroups;
    console.log('update payload =>', payloadRequest);
    return payloadRequest;
  }

  CancelAutomation() {
    this.createAutomationForm.reset();
    this.router.navigateByUrl('/automation');
  }

  timeout(ms: any) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  isActionSelected() {
    return this.actionsTableDataNew.length > 0;
  }

  findIndexByObjectProperty<T>(
    array: T[],
    searchObject: T,
    property: keyof T
  ): number {
    return array.findIndex((item) => item[property] === searchObject[property]);
  }

  confirmDialog() {
    this.translateService
      .get('AUTOMATION.CREATE.CONIFRM_INFO')
      .subscribe((translations) => {
        this.confirmationService.confirm({
          header: 'Confirmation',
          message: translations,
          icon: 'fa fa-exclamation-triangle',
          accept: () => {
            this.clearAllActions();
          },
          reject: (type: any) => {
            switch (type) {
              case ConfirmEventType.REJECT:
                break;
              case ConfirmEventType.CANCEL:
                break;
            }
          },
        });
      });
  }

  isObjectNullOrEmpty(obj: any): boolean {
    // Check if the object is null or undefined
    if (obj === null || obj === undefined) {
      return true;
    }

    // Check if the object is an empty object
    if (typeof obj === 'object' && Object.keys(obj).length === 0) {
      return true;
    }

    // Check if the object is an empty array
    if (Array.isArray(obj) && obj.length === 0) {
      return true;
    }

    return false;
  }

  formatScheduleTime(scheduleInMinutes: number, automationType:string): string {
    let timing:string="";
    this.translateService.get('AUTOMATION.EVENT.'+automationType)
    .subscribe((translation) => {
      timing = `(${scheduleInMinutes > 0 ? 'After' : scheduleInMinutes < 0 ?  'Before' : 'immidiate' } ${translation})`;
    });
    const absMinutes = Math.abs(scheduleInMinutes);
    let formattedTime: string ="";
    if (scheduleInMinutes != 0) {
      if (absMinutes % 1440 === 0) {
        const key = absMinutes / 1440  > 1 ? 'Days' : 'Day';
        formattedTime = `${absMinutes / 1440} ${key} ${timing}`;
      } else if (absMinutes % 60 === 0) {
        const key = absMinutes / 60  > 1 ? 'hours' : 'Hour';
        formattedTime = `${absMinutes / 60} ${key} ${timing}`;
      } else {
        formattedTime = `${absMinutes} Minutes ${timing}`;
      }
    }
    else
    {
      formattedTime = `Immediate`;
    }
    return formattedTime;
  }

  get StaffActions() {
    return this.Actions.filter(
      (a) => a.notificationTo == SendNotificationTo.Staff
    );
  }

  get ClientActions() {
    return this.Actions.filter(
      (a) => a.notificationTo == SendNotificationTo.Client
    );
  }

  get calendarEvent() {
    return this.calendarEvents$.value;
  }

  get getEmailActipnPayload() {
    return new BehaviorSubject<EmailActionPayload>(this.emailActionPayload!);
  }

  get getSmsActipnPayload() {
    return new BehaviorSubject<SmsActionPayload>(this.smsActionPayload!);
  }

  get getWhatsappActipnPayload() {
    return new BehaviorSubject<WhatsappActionPayload>(
      this.whatsAppActionPayload!
    );
  }

  get getTaskActipnPayload() {
    return new BehaviorSubject<TaskActionPayload>(this.taskActionPayload!);
  }

  get ActionTableData() {
    return this.actionsTableDataNew.filter((o) => o.configured);
  }

  get AutomationNameFormControl(): UntypedFormControl {
    return this.createAutomationForm.controls[
      'automation_name'
    ] as UntypedFormControl;
  }

  get AutomationDescriptionFormControl(): UntypedFormControl {
    return this.createAutomationForm.controls[
      'automation_description'
    ] as UntypedFormControl;
  }

  get CampaignActionDateFormControl(): UntypedFormControl {
    return this.createAutomationForm.controls[
      'campaign_action_date'
    ] as UntypedFormControl;
  }

  get SelectedActionsFormControl(): UntypedFormControl {
    return this.createAutomationForm.controls[
      'selected_actions'
    ] as UntypedFormControl;
  }

  get SelectedTenantsFormControl(): UntypedFormControl {
    return this.createAutomationForm.controls[
      'selected_tenant'
    ] as UntypedFormControl;
  }

  get RuleFormControl(): UntypedFormControl {
    return this.createAutomationForm.controls['rule'] as UntypedFormControl;
  }

  get SelectedAutomationTypeFormControl(): UntypedFormControl {
    return this.createAutomationForm.controls[
      'automation_type'
    ] as UntypedFormControl;
  }

  get getSelectedAutomationType() {
    return new BehaviorSubject(this.SelectedAutomationTypeFormControl.value);
  }

  public get ownerShipLevel() {
    return OwnershipLevel;
  }

  public get notificationTo() {
    return SendNotificationTo;
  }

  public get showSkeleton() {
    return this.showSkeleton$.value;
  }

  getEnumValue(key: string): OwnershipLevel {
    switch (key.toLowerCase()) {
      case 'tenant':
        return OwnershipLevel.Tenant;
      case 'company':
        return OwnershipLevel.Company;
      case 'franchise':
        return OwnershipLevel.Franchise;
     default:
       return OwnershipLevel.Tenant;
    }
  }

  public onEventClick(args: EventClickArgs): void {
    // Use type assertion to access the event data properly
    if (args.event) {
      // The event object structure depends on Syncfusion implementation
      // We need to cast it to any to access its properties safely
      const eventData = args.event as any;
      
      if (eventData && eventData.id) {
        // Find the action in actionsTableDataNew that corresponds to the clicked event
        const actionData = this.actionsTableDataNew.find(item => item.uuid === eventData.id);
        if (actionData) {
          this.configureActions(actionData.key, actionData.notificationTo, actionData, 'update');
        }
      }
    }
  }

  onEventRendered(args: EventRenderedArgs): void {
    args.element.style.height = 'auto';
    if(args.data['subject'] == '')
      args.element.style.backgroundColor = 'transparent';
  }

}
